import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Info,
  Loader2,
  TriangleAlert,
  XCircle,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Alert } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Select } from "@/components/ui/select"
import { consumer } from "@/lib/actioncable"
import type { PageProps } from "@/types/inertia"

const MONTHS_SHORT = ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"]

type UploadStatus = "pending" | "processing" | "completed" | "failed" | "cancelled"
type ValidationStatus = "passed" | "passed_with_warnings" | "failed" | null

type DuplicationFinding = {
  is_duplicate: boolean
  duplicate_of_upload_id: number | null
  duplicate_of_filename: string | null
  fingerprint: string | null
}

type MecePeriod = {
  year: number
  month: number
  area_total: string
  summary_total: string
  abs_gap: string
  pct_gap: string | null
  within_tolerance: boolean
}

type MeceFinding = {
  applicable: boolean
  comparable: boolean
  within_tolerance: boolean | null
  tolerance_pct: number
  area_total: string
  summary_total: string
  abs_gap: string
  pct_gap: string | null
  periods: MecePeriod[]
}

type GenderFinding = {
  tagged_gender: string
  actual_genders: string[]
  aligned: boolean
}

type ValidationReport = {
  version?: number
  duplication?: DuplicationFinding
  mece?: MeceFinding
  gender?: GenderFinding
}

type UploadDetail = {
  id: number
  filename: string
  gender: string
  dimension_type: string
  dimension_value: string
  status: UploadStatus
  row_count: number | null
  error_message: string | null
  imported_at: string | null
  created_at: string
  uploaded_by: string | null
  validation_status: ValidationStatus
  validation_report: ValidationReport
  validated_at: string | null
}

type Readiness = {
  ready: boolean
  inputs: { summary: boolean; minimarket: boolean; hyper_super: boolean; hb_cosmetic: boolean }
  present: string[]
  missing: string[]
}

type ReconciledResult = { written: number; channels: Record<string, string> }

type PeriodOption = { year: number; month: number; key: string }

interface ShowProps {
  upload: UploadDetail
  periods: PeriodOption[]
  readiness_by_period: Record<string, Readiness>
  reconciled_by_period: Record<string, ReconciledResult>
  reconciliation_enabled: boolean
  duplicate_of: { id: number; filename: string } | null
}

type ReconcilePhase = "idle" | "running" | "complete" | "failed"

type ReconBroadcast =
  | { type: "reconciliation_started"; upload_id: number; period_year: number; period_month: number }
  | { type: "reconciliation_progress"; upload_id: number; written: number }
  | {
      type: "reconciliation_complete"
      upload_id: number
      period_year: number
      period_month: number
      written: number
      gt_share: string | null
      summary_total: string | null
    }
  | { type: "reconciliation_failed"; upload_id: number; reason: string; error_message: string }
  | { type: string; upload_id: number }

const READINESS_INPUTS: { key: keyof Readiness["inputs"]; label: string }[] = [
  { key: "summary", label: "Summary" },
  { key: "minimarket", label: "Minimarket" },
  { key: "hyper_super", label: "Hyper/Super" },
  { key: "hb_cosmetic", label: "H&B Cosmetic" },
]

export default function Show({
  upload,
  periods,
  readiness_by_period,
  reconciled_by_period,
  reconciliation_enabled,
  duplicate_of,
}: ShowProps) {
  const { props } = usePage<PageProps>()
  const reconcileError = props.errors?.reconciliation
  const notice = props.flash?.notice

  const [selectedKey, setSelectedKey] = React.useState<string>(periods[0]?.key ?? "")
  const [phase, setPhase] = React.useState<ReconcilePhase>("idle")
  const [written, setWritten] = React.useState<number>(0)
  const [completeInfo, setCompleteInfo] = React.useState<
    { written: number; gt_share: string | null } | null
  >(null)
  const [failMessage, setFailMessage] = React.useState<string | null>(null)

  const selectedPeriod = periods.find((p) => p.key === selectedKey) ?? null
  const readiness = selectedKey ? readiness_by_period[selectedKey] : undefined
  const existingResult = selectedKey ? reconciled_by_period[selectedKey] : undefined

  // Subscribe to live reconciliation progress for this upload.
  React.useEffect(() => {
    const sub = consumer.subscriptions.create(
      { channel: "NiqFragranceUploadChannel", upload_id: upload.id },
      {
        received(data: ReconBroadcast) {
          if (data.upload_id !== upload.id) return
          switch (data.type) {
            case "reconciliation_started":
              setPhase("running")
              setWritten(0)
              setCompleteInfo(null)
              setFailMessage(null)
              break
            case "reconciliation_progress":
              setPhase("running")
              setWritten((data as { written: number }).written ?? 0)
              break
            case "reconciliation_complete": {
              const d = data as { written: number; gt_share: string | null }
              setPhase("complete")
              setCompleteInfo({ written: d.written, gt_share: d.gt_share })
              // Refresh persisted reconciled totals without disturbing local state.
              router.reload({ only: ["reconciled_by_period", "upload"] })
              break
            }
            case "reconciliation_failed":
              setPhase("failed")
              setFailMessage((data as { error_message: string }).error_message ?? "Terjadi kesalahan.")
              break
          }
        },
      }
    )
    return () => {
      sub.unsubscribe()
    }
  }, [upload.id])

  function handleReconcile() {
    if (!selectedPeriod || !readiness?.ready) return
    setPhase("running")
    setWritten(0)
    setCompleteInfo(null)
    setFailMessage(null)
    router.post(
      `/admin/niq-fragrances/${upload.id}/reconcile`,
      { period_year: selectedPeriod.year, period_month: selectedPeriod.month },
      { preserveState: true, preserveScroll: true },
    )
  }

  const report = upload.validation_report ?? {}
  const isValidated = !!upload.validation_status

  return (
    <>
      <Head title={upload.filename}>
        <meta
          name="description"
          content="Detail upload NIQ Fragrances: panel validasi (MECE, duplikasi, kesesuaian gender) dan pemicu rekonsiliasi channel dengan progres langsung."
        />
        <meta property="og:title" content={`Detail Upload — ${upload.filename}`} />
        <meta
          property="og:description"
          content="Lihat hasil validasi integritas dan jalankan rekonsiliasi pro-rata channel untuk satu periode + gender."
        />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <Link href="/admin/niq-fragrances" className="hover:text-ink-body">Data</Link>
          <span>/</span>
          <Link href="/admin/niq-fragrances" className="hover:text-ink-body">NIQ Fragrances</Link>
          <span>/</span>
          <span className="text-ink-body font-medium truncate max-w-[16rem]" title={upload.filename}>
            {upload.filename}
          </span>
        </div>

        <Link
          href="/admin/niq-fragrances"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink-body"
        >
          <ArrowLeft className="h-4 w-4" />
          Kembali ke daftar
        </Link>

        {/* Header */}
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="break-all">{upload.filename}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
                <Badge tone="neutral"><span className="capitalize">{upload.gender}</span></Badge>
                <Badge tone="neutral"><span className="capitalize">{upload.dimension_type}</span></Badge>
                <Badge tone="muted">{upload.dimension_value}</Badge>
                <UploadStatusBadge status={upload.status} />
              </div>
            </div>
            <ValidationStatusBadge status={upload.validation_status} />
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            <MetaItem label="Baris diimport" value={upload.row_count != null ? upload.row_count.toLocaleString("id-ID") : "—"} />
            <MetaItem label="Periode" value={periods.length > 0 ? `${formatPeriod(periods[periods.length - 1])} – ${formatPeriod(periods[0])}` : "—"} />
            <MetaItem label="Diunggah oleh" value={upload.uploaded_by ?? "—"} />
            <MetaItem label="Divalidasi" value={upload.validated_at ? formatDate(upload.validated_at) : "—"} />
          </dl>
        </div>

        {notice && (
          <Alert tone="info" icon={<Info className="h-4 w-4" />} className="mt-6">
            {notice}
          </Alert>
        )}

        {/* Validation panel */}
        <section className="mt-8">
          <h2 className="mb-1 text-lg font-bold text-ink-display">Panel Validasi</h2>
          <p className="mb-4 text-sm text-ink-muted">
            Hasil pemeriksaan integritas yang dijalankan otomatis setelah import.
          </p>

          {!isValidated ? (
            <Alert tone="info" icon={<Info className="h-4 w-4" />} title="Belum divalidasi">
              Upload ini belum memiliki hasil validasi. Validasi berjalan otomatis setelah import selesai.
            </Alert>
          ) : (
            <div className="space-y-3">
              <DuplicationCard finding={report.duplication} duplicateOf={duplicate_of} />
              <MeceCard finding={report.mece} />
              <GenderCard finding={report.gender} />
            </div>
          )}
        </section>

        {/* Reconciliation */}
        <section className="mt-10">
          <h2 className="mb-1 text-lg font-bold text-ink-display">Rekonsiliasi Channel</h2>
          <p className="mb-4 text-sm text-ink-muted">
            Rekonstruksi General Trade sebagai channel residual dan alokasi pro-rata setiap SKU
            ke Minimarket / Hyper-Super / H&amp;B / GT untuk gender{" "}
            <strong className="capitalize">{upload.gender}</strong> pada periode terpilih.
          </p>

          {!reconciliation_enabled ? (
            <Alert tone="info" icon={<Info className="h-4 w-4" />} title="Rekonsiliasi dinonaktifkan">
              Fitur rekonsiliasi sedang dimatikan (kill-switch). Tidak ada perubahan data yang dilakukan.
            </Alert>
          ) : periods.length === 0 ? (
            <Alert tone="info" icon={<Info className="h-4 w-4" />} title="Tidak ada periode">
              Upload ini belum memiliki record dengan periode, sehingga rekonsiliasi tidak dapat dijalankan dari sini.
            </Alert>
          ) : (
            <div className="rounded-md border border-hairline bg-surface p-4 sm:p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-ink-display">Periode</span>
                  <Select value={selectedKey} onChange={(e) => setSelectedKey(e.target.value)}>
                    {periods.map((p) => (
                      <option key={p.key} value={p.key}>{formatPeriod(p)}</option>
                    ))}
                  </Select>
                </label>

                <div>
                  <span className="mb-1 block text-sm font-medium text-ink-display">Kesiapan data sumber</span>
                  <div className="flex flex-wrap gap-2">
                    {READINESS_INPUTS.map(({ key, label }) => {
                      const ok = readiness?.inputs?.[key]
                      return (
                        <Badge key={key} tone={ok ? "success" : "danger"}>
                          {ok ? <CheckCircle2 className="mr-1 h-3 w-3" /> : <XCircle className="mr-1 h-3 w-3" />}
                          {label}
                        </Badge>
                      )
                    })}
                  </div>
                </div>
              </div>

              {reconcileError && (
                <Alert tone="danger" icon={<XCircle className="h-4 w-4" />} className="mt-4">
                  {reconcileError}
                </Alert>
              )}

              {readiness && !readiness.ready && (
                <Alert tone="warning" icon={<TriangleAlert className="h-4 w-4" />} className="mt-4" title="Data sumber belum lengkap">
                  Lengkapi dulu: {readiness.missing.join(", ")}.
                </Alert>
              )}

              {phase === "running" && (
                <Alert tone="info" icon={<Loader2 className="h-4 w-4 animate-spin" />} className="mt-4" title="Rekonsiliasi berjalan…">
                  {written > 0 ? `${written.toLocaleString("id-ID")} baris ditulis…` : "Menyiapkan alokasi…"}
                </Alert>
              )}

              {phase === "complete" && completeInfo && (
                <Alert tone="success" icon={<CheckCircle2 className="h-4 w-4" />} className="mt-4" title="Rekonsiliasi selesai">
                  {completeInfo.written.toLocaleString("id-ID")} baris reconciled ditulis
                  {completeInfo.gt_share != null && ` · GT share ${formatPercent(completeInfo.gt_share)}`}.
                </Alert>
              )}

              {phase === "failed" && (
                <Alert tone="danger" icon={<XCircle className="h-4 w-4" />} className="mt-4" title="Rekonsiliasi gagal">
                  {failMessage}
                </Alert>
              )}

              {existingResult && phase !== "running" && (
                <div className="mt-4 rounded-md border border-hairline bg-page p-3">
                  <p className="text-xs font-medium text-ink-muted">
                    Hasil rekonsiliasi tersimpan untuk {selectedPeriod ? formatPeriod(selectedPeriod) : ""}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-ink-body">
                    {Object.entries(existingResult.channels).map(([name, total]) => (
                      <span key={name}>
                        {name}: <strong className="tabular-nums">{formatNumber(total)}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5">
                <Button
                  variant="primary"
                  onClick={handleReconcile}
                  disabled={!readiness?.ready || phase === "running"}
                >
                  {phase === "running" ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Memproses…
                    </>
                  ) : (
                    "Jalankan Rekonsiliasi"
                  )}
                </Button>
              </div>
            </div>
          )}
        </section>
      </AppShell>
    </>
  )
}

function MetaItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-ink-body">{value}</dd>
    </div>
  )
}

function DuplicationCard({
  finding,
  duplicateOf,
}: {
  finding?: DuplicationFinding
  duplicateOf: { id: number; filename: string } | null
}) {
  if (!finding) return null
  if (!finding.is_duplicate) {
    return (
      <Alert tone="success" icon={<CheckCircle2 className="h-4 w-4" />} title="Tidak ada duplikasi">
        Sidik jari konten unik — tidak ada upload lain dengan data identik.
      </Alert>
    )
  }
  return (
    <Alert tone="danger" icon={<XCircle className="h-4 w-4" />} title="File duplikat terdeteksi">
      Data file ini identik dengan{" "}
      {duplicateOf ? (
        <Link href={`/admin/niq-fragrances/${duplicateOf.id}`} className="font-medium underline">
          {duplicateOf.filename}
        </Link>
      ) : (
        <strong>{finding.duplicate_of_filename ?? "upload lain"}</strong>
      )}
      . Periksa apakah file ini salah ditandai (mis. Male vs Female).
    </Alert>
  )
}

function MeceCard({ finding }: { finding?: MeceFinding }) {
  if (!finding) return null
  if (!finding.applicable) {
    return (
      <Alert tone="info" icon={<Info className="h-4 w-4" />} title="MECE — tidak berlaku">
        Pemeriksaan MECE hanya untuk upload bertipe <strong>Area</strong>.
      </Alert>
    )
  }
  if (!finding.comparable) {
    return (
      <Alert tone="info" icon={<Info className="h-4 w-4" />} title="MECE — belum bisa dibandingkan">
        Belum ada data Summary untuk gender ini sebagai pembanding.
      </Alert>
    )
  }

  const pass = finding.within_tolerance
  return (
    <Alert
      tone={pass ? "success" : "warning"}
      icon={pass ? <CheckCircle2 className="h-4 w-4" /> : <TriangleAlert className="h-4 w-4" />}
      title={pass ? "MECE — sesuai" : "MECE — selisih melebihi toleransi"}
    >
      <p>
        Total area <strong className="tabular-nums">{formatNumber(finding.area_total)}</strong> vs
        summary <strong className="tabular-nums">{formatNumber(finding.summary_total)}</strong> —
        selisih <strong className="tabular-nums">{formatNumber(finding.abs_gap)}</strong>
        {finding.pct_gap != null && ` (${formatPercent2(finding.pct_gap)})`}, toleransi {finding.tolerance_pct}%.
      </p>
      {finding.periods.length > 0 && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-ink-muted">
                <th className="py-1 pr-3 text-left font-medium">Periode</th>
                <th className="py-1 pr-3 text-right font-medium">Area</th>
                <th className="py-1 pr-3 text-right font-medium">Summary</th>
                <th className="py-1 pr-3 text-right font-medium">Selisih</th>
                <th className="py-1 text-right font-medium">%</th>
              </tr>
            </thead>
            <tbody>
              {finding.periods.map((p) => (
                <tr key={`${p.year}-${p.month}`} className={p.within_tolerance ? "" : "text-signal-display"}>
                  <td className="py-1 pr-3">{formatPeriod([p.year, p.month])}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{formatNumber(p.area_total)}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{formatNumber(p.summary_total)}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{formatNumber(p.abs_gap)}</td>
                  <td className="py-1 text-right tabular-nums">{p.pct_gap != null ? formatPercent2(p.pct_gap) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Alert>
  )
}

function GenderCard({ finding }: { finding?: GenderFinding }) {
  if (!finding) return null
  if (finding.aligned) {
    return (
      <Alert tone="success" icon={<CheckCircle2 className="h-4 w-4" />} title="Gender sesuai">
        Nilai GENDER di dalam file konsisten dengan tag upload ({finding.tagged_gender}).
      </Alert>
    )
  }
  return (
    <Alert tone="warning" icon={<TriangleAlert className="h-4 w-4" />} title="Ketidaksesuaian gender">
      Ditandai <strong className="capitalize">{finding.tagged_gender}</strong>, tetapi baris file berisi{" "}
      <strong className="uppercase">{finding.actual_genders.join(", ")}</strong>.
    </Alert>
  )
}

function ValidationStatusBadge({ status }: { status: ValidationStatus }) {
  switch (status) {
    case "passed":
      return <Badge tone="success"><CheckCircle2 className="mr-1 h-3 w-3" />Validasi lolos</Badge>
    case "passed_with_warnings":
      return <Badge tone="signal"><TriangleAlert className="mr-1 h-3 w-3" />Lolos dengan peringatan</Badge>
    case "failed":
      return <Badge tone="danger"><XCircle className="mr-1 h-3 w-3" />Validasi gagal</Badge>
    default:
      return <Badge tone="muted"><Clock className="mr-1 h-3 w-3" />Belum divalidasi</Badge>
  }
}

function UploadStatusBadge({ status }: { status: UploadStatus }) {
  switch (status) {
    case "pending":
      return <Badge tone="muted"><Clock className="mr-1 h-3 w-3" />Pending</Badge>
    case "processing":
      return <Badge tone="accent"><Loader2 className="mr-1 h-3 w-3 animate-spin" />Processing</Badge>
    case "completed":
      return <Badge tone="success"><CheckCircle2 className="mr-1 h-3 w-3" />Completed</Badge>
    case "failed":
      return <Badge tone="danger"><XCircle className="mr-1 h-3 w-3" />Failed</Badge>
    case "cancelled":
      return <Badge tone="muted">Dibatalkan</Badge>
  }
}

function formatPeriod(period: [number, number] | PeriodOption): string {
  const [y, m] = Array.isArray(period) ? period : [period.year, period.month]
  return `${MONTHS_SHORT[m]} ${y}`
}

function formatNumber(value: string): string {
  const n = Number(value)
  if (Number.isNaN(n)) return value
  return n.toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatPercent(value: string): string {
  const n = Number(value)
  if (Number.isNaN(n)) return value
  return `${(n * 100).toLocaleString("id-ID", { maximumFractionDigits: 2 })}%`
}

function formatPercent2(value: string): string {
  const n = Number(value)
  if (Number.isNaN(n)) return value
  return `${n.toLocaleString("id-ID", { maximumFractionDigits: 3 })}%`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })
}
